import { Injectable, computed, effect, inject, signal } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { RouterStateSnapshot, TitleStrategy } from '@angular/router'
import { NavContextService } from './nav-context.service'

const APP = 'SCIOS'

/**
 * Names every page in the browser tab and in history, e.g. "Stats · APOEL | SCIOS" or
 * "Overview · APOEL v Omonoia | SCIOS". Before this every entry read "Science of Sports",
 * so the long-press Back list on a phone was a column of identical lines.
 *
 * The section comes from the route's static `title`. The entity name arrives later, from
 * the resolver-fed nav context each entity layout publishes (and clears on leaving), so the
 * title is recomputed when it lands; the browser updates the current history entry's title
 * when document.title changes.
 */
@Injectable({ providedIn: 'root' })
export class PageTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title)
  private readonly nav = inject(NavContextService)
  private readonly section = signal<string | undefined>(undefined)

  private readonly full = computed(() => {
    const ctx = this.nav.snapshot()
    const entity = ctx.versus && ctx.entityLabel2
      ? `${ctx.entityLabel} v ${ctx.entityLabel2}`
      : ctx.entityLabel
    const parts = [this.section(), entity].filter((p) => !!p)
    return parts.length ? `${parts.join(' · ')} | ${APP}` : APP
  })

  constructor() {
    super()
    effect(() => this.title.setTitle(this.full()))
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.section.set(this.buildTitle(snapshot))
  }
}
